/**
 * Dynamic Configuration Loader for SmartTrader
 * Loads domain-specific configuration from Platform Dashboard API
 *
 * This enables white-label SmartTrader sites with custom branding,
 * colors, and feature toggles based on the domain.
 */

// Platform Dashboard API configuration
const PLATFORM_API_BASE =
  process.env.PLATFORM_API_BASE || "https://dforge.site";
const CONFIG_API_ENDPOINT = `${PLATFORM_API_BASE}/api/sites/smarttrader-config`;
const DEFAULT_SERVER_URL = "api.derivws.com/trading/v1/options/ws/public";

// Cache configuration
// 5 minutes: matches the server's s-maxage CDN window. Staleness is covered by
// the visibilitychange listener (bottom of file) which refreshes the moment a
// user returns to the tab, so editor changes still feel instant.
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in memory
const LOCAL_STORAGE_KEY = "smarttrader_config_cache";
const LOCAL_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes localStorage

// In-memory cache
let configCache = null;
let cacheTimestamp = 0;
let pendingRequest = null;

function normalizeServerUrl(serverUrl) {
  let value = typeof serverUrl === "string" ? serverUrl.trim() : "";
  value = value
    .replace(/^wss?:\/\//i, "")
    .split(/[?#]/)[0]
    .replace(/\/+$/, "");

  if (!value || value === "ws.derivws.com") {
    return DEFAULT_SERVER_URL;
  }

  const host = value.split("/")[0].toLowerCase();
  const normalizedHost = host.replace(/^www\./i, "");
  const currentHost =
    typeof window !== "undefined"
      ? window.location.hostname.toLowerCase().replace(/^www\./i, "")
      : "";
  const isAllowedDerivHost =
    /(^|\.)derivws\.com$/i.test(host) || /(^|\.)deriv\.dev$/i.test(host);

  if (normalizedHost === currentHost || !isAllowedDerivHost) {
    console.warn(
      `[SmartTrader Config] Ignoring invalid websocket serverUrl "${serverUrl}", using ${DEFAULT_SERVER_URL}`,
    );
    return DEFAULT_SERVER_URL;
  }

  return value;
}

function normalizeConfig(config) {
  if (!config) return config;
  return {
    ...config,
    serverUrl: normalizeServerUrl(config.serverUrl),
  };
}

/**
 * Get current domain from window.location
 * @returns {string} Current domain (e.g., "example.com")
 */
function getCurrentDomain() {
  return window.location.host;
}

/**
 * Check if config is a valid working config (not an error state)
 * @param {Object} config - Config to check
 * @returns {boolean} Whether config is valid
 */
function isValidConfig(config) {
  if (!config) return false;
  // Error states should not be considered valid for caching
  if (config.isNotConfigured || config.isSuspended) return false;
  // Must have a real appId (not default) or a custom siteName
  return (
    config.appId !== 22168 ||
    !!config.appIdV2 ||
    (config.siteName && config.siteName !== "SmartTrader")
  );
}

/**
 * Get cached config from localStorage
 * @returns {Object|null} Cached config or null
 */
function getLocalStorageCache() {
  try {
    const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!cached) return null;

    const { config, timestamp, domain } = JSON.parse(cached);

    // Check if cache is for current domain, not expired, and is valid (not error state)
    if (
      domain === getCurrentDomain() &&
      Date.now() - timestamp < LOCAL_CACHE_DURATION &&
      isValidConfig(config)
    ) {
      return config;
    }

    // Clear expired or invalid cache
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    return null;
  } catch (error) {
    console.error(
      "[SmartTrader Config] Error reading localStorage cache:",
      error,
    );
    return null;
  }
}

/**
 * Save config to localStorage cache
 * Only caches valid configs (not error states)
 * @param {Object} config - Configuration to cache
 */
function setLocalStorageCache(config) {
  // Don't cache error states (not configured, suspended)
  if (!isValidConfig(config)) {
    console.log(
      "[SmartTrader Config] Skipping localStorage cache for error/default config",
    );
    return;
  }

  try {
    const cacheData = {
      config,
      timestamp: Date.now(),
      domain: getCurrentDomain(),
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.error(
      "[SmartTrader Config] Error writing localStorage cache:",
      error,
    );
  }
}

/**
 * Fetch domain configuration from Platform Dashboard API
 * @param {string} domain - Domain to fetch config for
 * @returns {Promise<Object>} Configuration object
 */
async function fetchDomainConfig(domain) {
  try {
    const url = new URL(CONFIG_API_ENDPOINT);
    url.searchParams.set("domain", domain);
    // NOTE: no `_t` cache-buster and no `no-store` — the dashboard serves
    // s-maxage=300 so Vercel's CDN can answer most of these without invoking
    // a function. A unique URL per request made every fetch a paid CDN miss.

    const response = await fetch(
      url.toString(),
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        // Allow CORS
        mode: "cors",
        cache: "default",
      },
    );

    if (!response.ok) {
      // For 404 or 405 (API route not found), return a "not found" response
      if (response.status === 404 || response.status === 405) {
        console.warn(
          `[SmartTrader Config] API returned ${response.status}, treating as not configured`,
        );
        return { found: false, config: null };
      }
      throw new Error(
        `API returned ${response.status}: ${response.statusText}`,
      );
    }

    const data = await response.json();
    if (data && data.config) {
      data.config = normalizeConfig(data.config);
    }
    return data;
  } catch (error) {
    console.error("[SmartTrader Config] Failed to load from API:", error);
    // Return "not found" for network errors instead of throwing
    return { found: false, config: null, error: error.message };
  }
}

/**
 * Check if domain is localhost
 * @param {string} domain
 * @returns {boolean}
 */
function isLocalhost(domain) {
  return domain.includes("localhost") || domain.includes("127.0.0.1");
}

/**
 * Get localhost config
 * @param {string} domain
 * @returns {Object}
 */
function getLocalhostConfig(domain) {
  const fallback =
    STATIC_FALLBACKS[domain] ||
    STATIC_FALLBACKS["localhost"] ||
    STATIC_FALLBACKS["localhost:3000"];
  return { ...fallback, domain };
}

/**
 * Get domain configuration with caching
 * @param {Object} options - Options
 * @param {boolean} options.forceRefresh - Force refresh from API
 * @returns {Promise<Object>} Configuration object
 */
async function getDomainConfig(options = {}) {
  const { forceRefresh = false } = options;
  const now = Date.now();
  const domain = getCurrentDomain();

  // Check for localhost development - try API first, but keep fallback ready
  // We removed the early return here to allow fetching dynamic config for localhost
  // if it exists on the server (Appwrite/DerivForge).

  // Return in-memory cached config if valid and not an error state (production only)
  if (
    !forceRefresh &&
    configCache &&
    now - cacheTimestamp < CACHE_DURATION &&
    isValidConfig(configCache)
  ) {
    console.log("[SmartTrader Config] Using memory cache");
    return configCache;
  }

  // Try localStorage cache if API fails (production only)
  if (!forceRefresh && !configCache) {
    const localCache = getLocalStorageCache();
    if (localCache) {
      console.log("[SmartTrader Config] Using localStorage cache");
      configCache = localCache;
      cacheTimestamp = now;
      return configCache;
    }
  }

  // Deduplicate concurrent requests
  if (pendingRequest) {
    console.log("[SmartTrader Config] Waiting for pending request");
    return pendingRequest;
  }

  // Create new request
  pendingRequest = fetchDomainConfig(domain)
    .then((data) => {
      // Check if site is not configured (API returned found: false or no config)
      if (!data || !data.found || !data.config) {
        // Special handling for localhost: If not found on server, use static fallback
        if (isLocalhost(domain)) {
          console.log(
            "[SmartTrader Config] Localhost not configured on server, using static fallback",
          );
          const localhostConfig = getLocalhostConfig(domain);
          configCache = localhostConfig;
          cacheTimestamp = now;
          return localhostConfig;
        }

        console.warn(`[SmartTrader Config] Domain not configured: ${domain}`);
        const notConfiguredConfig = {
          ...getDefaultConfig(),
          domain,
          siteName: domain,
          siteTitle: `${domain} - Site Not Configured`,
          isNotConfigured: true,
          isSuspended: false,
        };
        // DON'T cache "not configured" state - allows retry after site is set up
        // Only use short memory cache to prevent hammering the API
        configCache = notConfiguredConfig;
        cacheTimestamp = now;
        // NOT caching to localStorage - error states should not persist
        return notConfiguredConfig;
      }

      const config = data.config;

      // Check if site is suspended
      if (config.isSuspended) {
        console.warn(`[SmartTrader Config] Site suspended: ${domain}`);
        const suspendedConfig = {
          ...config,
          domain,
          isSuspended: true,
          isNotConfigured: false,
          suspendedReason:
            config.suspendedReason ||
            "This site has been suspended by the administrator.",
        };
        // DON'T cache suspended state to localStorage - allows retry after unsuspension
        configCache = suspendedConfig;
        cacheTimestamp = now;
        // NOT caching to localStorage - suspended states should not persist
        return suspendedConfig;
      }

      // Normal config - Update caches
      const validConfig = {
        ...config,
        domain,
        isNotConfigured: false,
        isSuspended: false,
      };
      configCache = validConfig;
      cacheTimestamp = now;
      setLocalStorageCache(validConfig);

      console.log(
        `[SmartTrader Config] Loaded config for: ${validConfig.siteName || domain}`,
      );
      return validConfig;
    })
    .catch((error) => {
      console.error("[SmartTrader Config] Error, using fallback:", error);

      // Check if we have cached config
      if (configCache) {
        console.log("[SmartTrader Config] Using stale cache due to error");
        return configCache;
      }

      // Return fallback config on error
      let fallback = getDefaultConfig();

      // If localhost error (e.g. API down), use localhost fallback
      if (isLocalhost(domain)) {
        console.log(
          "[SmartTrader Config] API error on localhost, using static fallback",
        );
        fallback = getLocalhostConfig(domain);
      }

      configCache = fallback;
      cacheTimestamp = now;
      return fallback;
    })
    .finally(() => {
      pendingRequest = null;
    });

  return pendingRequest;
}

/**
 * Get configuration synchronously (returns cached or default)
 * @returns {Object} Configuration object
 */
function getDomainConfigSync() {
  const domain = getCurrentDomain();

  // Check localhost first
  if (isLocalhost(domain)) {
    return getLocalhostConfig(domain);
  }

  if (configCache) {
    return configCache;
  }

  // Try localStorage
  const localCache = getLocalStorageCache();
  if (localCache) {
    configCache = localCache;
    return configCache;
  }

  // Return default config for initial render
  return getDefaultConfig();
}

/**
 * Localhost static fallback configurations
 * Used for local development when API is not accessible
 */
const STATIC_FALLBACKS = {
  localhost: {
    siteName: "SmartTrader Dev",
    siteTitle: "SmartTrader - Local Development",
    siteDescription: "Local development environment",
    appId: 22168,
    appIdV2: process.env.DERIV_V2_CLIENT_ID || '',
    serverUrl: DEFAULT_SERVER_URL,
    primaryColor: "#6b21a8", // Deep purple
    secondaryColor: "#a855f7", // Light purple
    accentColor: "#c084fc", // Bright purple accent
    textColor: "#1f2937", // Dark gray for readability
    backgroundColor: "#faf5ff", // Very light purple tint
    headerBgColor: "#581c87", // Dark purple header
    footerBgColor: "#f3e8ff", // Light purple footer
    logoPath: "/images/pages/header/logo_smart_trader.svg",
    faviconPath: "/images/common/logos/icon_smarttrader.svg",
    signupUrl: "https://track.deriv.com/_9uoxxa19kFmFfUyb_9NCN2Nd7ZgqdRLk/1/", // Added for testing
    showHeader: true,
    showFooter: true,
    features: {
      portfolio: true,
      realityCheck: true,
      liveChat: true,
      chart: true,
      analysis: true,
      explanation: true,
      earnings: true,
      limitStopLoss: true,
      digits: true,
      dfunded: true,
    },
    // DFunded login button in the header (dashboard dfundedLoginButton).
    dfundedLoginButton: true,
    socialLinks: {},
    footerLinks: [],
    environment: "development",
    isSuspended: false,
    isNotConfigured: false,
  },
  "localhost:3000": {
    siteName: "SmartTrader Dev",
    siteTitle: "SmartTrader - Local Development",
    siteDescription: "Local development environment",
    appId: 22168,
    appIdV2: process.env.DERIV_V2_CLIENT_ID || '',
    serverUrl: DEFAULT_SERVER_URL,
    primaryColor: "#6b21a8", // Deep purple
    secondaryColor: "#a855f7", // Light purple
    accentColor: "#c084fc", // Bright purple accent
    textColor: "#1f2937", // Dark gray for readability
    backgroundColor: "#faf5ff", // Very light purple tint
    headerBgColor: "#581c87", // Dark purple header
    footerBgColor: "#f3e8ff", // Light purple footer
    logoPath: "/images/pages/header/logo_smart_trader.svg",
    faviconPath: "/images/common/logos/icon_smarttrader.svg",
    signupUrl: "https://track.deriv.com/_9uoxxa19kFmFfUyb_9NCN2Nd7ZgqdRLk/1/", // Added for testing
    showHeader: true,
    showFooter: true,
    features: {
      portfolio: true,
      realityCheck: true,
      liveChat: true,
      chart: true,
      analysis: true,
      explanation: true,
      earnings: true,
      limitStopLoss: true,
      digits: true,
      dfunded: true,
    },
    // DFunded login button in the header (dashboard dfundedLoginButton).
    dfundedLoginButton: true,
    socialLinks: {},
    footerLinks: [],
    environment: "development",
    isSuspended: false,
    isNotConfigured: false,
  },
};

/**
 * Default fallback configuration
 * @returns {Object} Default configuration
 */
function getDefaultConfig() {
  return {
    domain: getCurrentDomain(),
    siteName: "SmartTrader",
    siteTitle: "SmartTrader - Online Trading Platform",
    siteDescription: "Trade forex, commodities, and synthetic indices",
    appId: 22168,
    appIdV2: '',
    serverUrl: DEFAULT_SERVER_URL,
    primaryColor: "#0e0e0e",
    secondaryColor: "#85acb0",
    accentColor: "#ff444f",
    textColor: "#333333",
    backgroundColor: "#ffffff",
    headerBgColor: "#0e0e0e",
    footerBgColor: "#f2f2f2",
    logoPath: "/images/pages/header/logo_smart_trader.svg",
    faviconPath: "/images/common/logos/icon_smarttrader.svg",
    signupUrl: "https://track.deriv.com/_9uoxxa19kFmFfUyb_9NCN2Nd7ZgqdRLk/1/", // Default tracking link
    showHeader: true,
    showFooter: true,
    features: {
      portfolio: true,
      realityCheck: true,
      liveChat: true,
      chart: true,
      analysis: true,
      explanation: true,
      earnings: true,
      limitStopLoss: true,
      digits: true,
      dfunded: true,
    },
    // DFunded login button in the header (dashboard dfundedLoginButton).
    dfundedLoginButton: true,
    socialLinks: {},
    footerLinks: [],
    environment: "production",
    isSuspended: false,
    isNotConfigured: false,
  };
}

/**
 * Apply dynamic theming to the page
 * @param {Object} config - Configuration object
 */
function applyDynamicTheme(config) {
  if (!config) return;

  // Set CSS custom properties on :root
  const root = document.documentElement;

  if (config.primaryColor) {
    root.style.setProperty("--brand-primary", config.primaryColor);
    root.style.setProperty("--smarttrader-primary", config.primaryColor);
  }
  if (config.secondaryColor) {
    root.style.setProperty("--brand-secondary", config.secondaryColor);
    root.style.setProperty("--smarttrader-secondary", config.secondaryColor);
    // Map secondary color to "success/up" indicators if theming is desired
    root.style.setProperty("--purchase-main-1", config.secondaryColor);
    root.style.setProperty("--text-profit-success", config.secondaryColor);
    root.style.setProperty("--status-success", config.secondaryColor);
  }
  if (config.accentColor) {
    root.style.setProperty("--brand-accent", config.accentColor);
    root.style.setProperty("--smarttrader-accent", config.accentColor);
    root.style.setProperty("--button-primary-bg", config.accentColor);
    // Fixes for buttons, price badges and "down/loss" indicators
    root.style.setProperty("--button-primary-default", config.accentColor);
    root.style.setProperty("--brand-red-coral", config.accentColor);
    root.style.setProperty("--purchase-main-2", config.accentColor);
    root.style.setProperty("--text-loss-danger", config.accentColor);
    root.style.setProperty("--status-danger", config.accentColor);
  }
  if (config.textColor) {
    root.style.setProperty("--text-color", config.textColor);
    root.style.setProperty("--text-primary", config.textColor);
  }
  if (config.backgroundColor) {
    root.style.setProperty("--background-color", config.backgroundColor);
    root.style.setProperty("--body-bg", config.backgroundColor);
  }
  if (config.headerBgColor) {
    root.style.setProperty("--header-bg", config.headerBgColor);
    root.style.setProperty("--navbar-bg", config.headerBgColor);
  }
  if (config.footerBgColor) {
    root.style.setProperty("--footer-bg", config.footerBgColor);
  }
  if (config.borderColor) {
    root.style.setProperty("--border-color", config.borderColor);
  }
  if (config.inputBgColor) {
    root.style.setProperty("--input-bg", config.inputBgColor);
  }

  // Update document title
  if (config.siteTitle) {
    document.title = config.siteTitle;
  }

  // Update favicon - use faviconPath if available, otherwise fallback to logoPath
  const faviconToUse = config.faviconPath || config.logoPath;
  if (faviconToUse) {
    updateFavicon(faviconToUse);
  }

  // Update logo
  if (config.logoPath) {
    updateLogo(config.logoPath, config.logoInversePath);
  }

  // Update meta description
  if (config.siteDescription) {
    updateMetaDescription(config.siteDescription);
  }

  // Update Signup Links in the DOM (for static elements)
  if (config.signupUrl) {
    const signupButtons = document.querySelectorAll(
      "#btn__signup, #authorization_error_btn_signup, .btn-signup",
    );
    signupButtons.forEach((btn) => {
      btn.href = config.signupUrl;
    });
  }
}

/**
 * Update favicon dynamically
 * Supports .ico, .png, .svg formats
 * @param {string} faviconPath - Path to favicon (can be favicon or logo as fallback)
 */
function updateFavicon(faviconPath) {
  if (!faviconPath) return;

  // Remove existing favicon links
  const existingLinks = document.querySelectorAll('link[rel*="icon"]');
  existingLinks.forEach((link) => link.remove());

  // Determine the type based on file extension
  let iconType = "image/x-icon";
  if (faviconPath.endsWith(".png")) {
    iconType = "image/png";
  } else if (faviconPath.endsWith(".svg")) {
    iconType = "image/svg+xml";
  } else if (faviconPath.endsWith(".gif")) {
    iconType = "image/gif";
  }

  // Create main favicon link
  const link = document.createElement("link");
  link.rel = "icon";
  link.type = iconType;
  link.href = faviconPath;
  document.head.appendChild(link);

  // Create shortcut icon for older browsers
  const shortcutLink = document.createElement("link");
  shortcutLink.rel = "shortcut icon";
  shortcutLink.href = faviconPath;
  document.head.appendChild(shortcutLink);

  // Create apple-touch-icon
  const appleLink = document.createElement("link");
  appleLink.rel = "apple-touch-icon";
  appleLink.href = faviconPath;
  document.head.appendChild(appleLink);

  console.log("[SmartTrader] Favicon updated:", faviconPath);
}

/**
 * Update logo dynamically
 * @param {string} logoPath - Path to logo
 * @param {string} logoInversePath - Path to inverse logo (for dark backgrounds)
 */
function updateLogo(logoPath, logoInversePath) {
  const logoElements = document.querySelectorAll(
    ".brand-logo, .header__logo, [data-logo]",
  );
  logoElements.forEach((el) => {
    const parentBg = window.getComputedStyle(el.parentElement).backgroundColor;
    const isDarkBg =
      parentBg &&
      (parentBg === "rgb(14, 14, 14)" ||
        parentBg === "rgb(0, 0, 0)" ||
        parentBg.startsWith("rgb(0,"));
    el.src = isDarkBg && logoInversePath ? logoInversePath : logoPath;
  });
}

/**
 * Update meta description
 * @param {string} description - Meta description
 */
function updateMetaDescription(description) {
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.appendChild(meta);
  }
  meta.content = description;
}

/**
 * Check if a feature is enabled
 * @param {string} featureName - Name of the feature
 * @returns {boolean} Whether feature is enabled
 */
function isFeatureEnabled(featureName) {
  const config = getDomainConfigSync();
  if (!config || !config.features) return true; // Default to enabled
  return config.features[featureName] !== false;
}

/**
 * DFunded sign-in on this site (the dashboard's features.dfunded switch).
 * On unless the owner explicitly switched it off: configs saved before the
 * switch existed have no key, and only an explicit `false` turns it off.
 * @param {Object} config - Site configuration
 * @returns {boolean} Whether DFunded is offered
 */
function isDFundedEnabledFor(config) {
  return !(config && config.features && config.features.dfunded === false);
}

/**
 * DFunded switch for the current site. Never throws: on any error DFunded
 * stays on, as it was before the switch existed.
 * @returns {boolean} Whether DFunded is offered on this site
 */
function isDFundedEnabled() {
  try {
    return isDFundedEnabledFor(getDomainConfigSync());
  } catch (error) {
    return true;
  }
}

/**
 * DFunded login button on this site (the dashboard's top-level
 * `dfundedLoginButton` flag). Only hides the sign-in entry point: unlike
 * `features.dfunded`, it never ignores a DFunded session, so a trader already
 * signed in keeps trading. Only an explicit `false` hides the button; a
 * config without the key (older responses) shows it.
 * @param {Object} config - Site configuration
 * @returns {boolean} Whether the DFunded login button is shown
 */
function isDFundedLoginButtonShownFor(config) {
  return !(config && config.dfundedLoginButton === false);
}

/**
 * DFunded login button switch for the current site. Never throws: on any
 * error the button stays shown, as it was before the switch existed.
 * @param {Function} [readConfig] - Reads the site config (tests pass their own)
 * @returns {boolean} Whether the DFunded login button is shown
 */
function isDFundedLoginButtonShown(readConfig = getDomainConfigSync) {
  try {
    return isDFundedLoginButtonShownFor(readConfig());
  } catch (error) {
    return true;
  }
}

/**
 * Get App ID from dynamic config
 * @returns {number} App ID
 */
function getAppId() {
  const config = getDomainConfigSync();
  if (config?.authMode === 'v2_oauth') return 22168;

  const appId = config?.appId;
  if (typeof appId === 'number') return appId;
  if (typeof appId === 'string' && /^\d+$/.test(appId)) return appId;
  return 22168;
}

/**
 * Get v2 OAuth client ID from dynamic config.
 * Returns empty string when site is not registered for v2.
 * @returns {string} v2 client ID or ''
 */
function getAppIdV2() {
  const config = getDomainConfigSync();
  return config?.appIdV2 || '';
}

/**
 * Get server URL from dynamic config
 * @returns {string} Server URL
 */
function getServerUrl() {
  const config = getDomainConfigSync();
  return normalizeServerUrl(config?.serverUrl);
}

/**
 * Redirect to error page
 * @param {string} page - Error page name ('site-not-configured' or 'site-suspended')
 * @param {string} reason - Optional reason for suspension
 */
function redirectToErrorPage(page, reason) {
  const currentPath = window.location.pathname;
  // Don't redirect if already on error page
  if (
    currentPath.includes("site-not-configured") ||
    currentPath.includes("site-suspended")
  ) {
    return;
  }

  let errorUrl = `/${page}.html`;
  if (reason) {
    errorUrl += `?reason=${encodeURIComponent(reason)}`;
  }

  console.log(`[SmartTrader] Redirecting to ${page} page`);
  window.location.replace(errorUrl);
}

// Guard to prevent multiple simultaneous initializations
let isInitializing = false;
let initPromise = null;

/**
 * Initialize dynamic configuration
 * @returns {Promise<Object>} Configuration object
 */
async function initDynamicConfig() {
  // Prevent double initialization
  if (isInitializing) {
    return initPromise;
  }

  isInitializing = true;

  initPromise = (async () => {
    try {
      const config = await getDomainConfig();

      // Check if site is suspended - redirect to suspended page
      if (config.isSuspended) {
        console.warn("[SmartTrader] Site is suspended, redirecting...");
        redirectToErrorPage("site-suspended", config.suspendedReason);
        return config;
      }

      // Check if site is not configured - redirect to not configured page
      if (config.isNotConfigured) {
        console.warn("[SmartTrader] Site not configured, redirecting...");
        redirectToErrorPage("site-not-configured");
        return config;
      }

      // Apply theme immediately
      applyDynamicTheme(config);

      // Store config globally for other modules
      window.SmartTraderConfig = config;

      // Dispatch event for other components
      window.dispatchEvent(
        new CustomEvent("smarttrader:configloaded", {
          detail: { config },
        }),
      );

      console.log("[SmartTrader] Dynamic config initialized:", config.siteName);

      return config;
    } catch (error) {
      console.error(
        "[SmartTrader] Failed to initialize dynamic config:",
        error,
      );
      // On critical error, redirect to not configured page instead of silent fallback
      redirectToErrorPage("site-not-configured");
      return null;
    } finally {
      isInitializing = false;
    }
  })();

  return initPromise;
}

/**
 * Background refresh — fetch fresh config and re-apply the theme.
 * Only acts on a positive, non-suspended result: a transient API/network
 * failure must NEVER flip an active trading session into the
 * "not configured" or "suspended" state (initDynamicConfig redirects on those).
 */
async function backgroundRefreshConfig() {
  try {
    const domain = getCurrentDomain();
    if (isLocalhost(domain)) return;
    const data = await fetchDomainConfig(domain);
    if (!data || !data.found || !data.config || data.config.isSuspended) return;
    const validConfig = {
      ...data.config,
      domain,
      isNotConfigured: false,
      isSuspended: false,
    };
    configCache = validConfig;
    cacheTimestamp = Date.now();
    setLocalStorageCache(validConfig);
    applyDynamicTheme(validConfig);
    window.SmartTraderConfig = validConfig;
    // Same event as the first load, so switches like features.dfunded
    // reach the header without a reload.
    window.dispatchEvent(
      new CustomEvent("smarttrader:configloaded", {
        detail: { config: validConfig },
      }),
    );
  } catch (error) {
    // Silent — background refresh must never disturb the session.
  }
}

// When a user returns to a tab whose cache window has lapsed, refresh in the
// background — keeps website-editor changes feeling instant despite the
// 5-minute cache TTL.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) return;
    if (Date.now() - cacheTimestamp >= CACHE_DURATION) {
      backgroundRefreshConfig();
    }
  });
}

/**
 * Reload configuration (useful for admin preview)
 * @returns {Promise<Object>} Fresh configuration
 */
async function reloadConfig() {
  configCache = null;
  cacheTimestamp = 0;
  localStorage.removeItem(LOCAL_STORAGE_KEY);
  return initDynamicConfig();
}

/**
 * Dev preview mode - enable for testing
 * @param {string} domain - Domain to preview
 */
function enableDevPreview(domain) {
  if (typeof window !== "undefined") {
    window.localStorage.setItem("smarttrader_preview_domain", domain);
    console.log(`[SmartTrader] Preview mode enabled for: ${domain}`);
    reloadConfig();
  }
}

// Make enableDevPreview globally available
if (typeof window !== "undefined") {
  window.enableSmartTraderPreview = enableDevPreview;
}

// Export for module systems
module.exports = {
  getDomainConfig,
  getDomainConfigSync,
  initDynamicConfig,
  applyDynamicTheme,
  getDefaultConfig,
  isFeatureEnabled,
  isDFundedEnabled,
  isDFundedEnabledFor,
  isDFundedLoginButtonShown,
  isDFundedLoginButtonShownFor,
  getAppId,
  getAppIdV2,
  getServerUrl,
  normalizeServerUrl,
  reloadConfig,
  enableDevPreview,
};
